import { apiClient, ApiError } from '../client';
import { API_CONFIG } from '../config';

const BASE_URL = `${API_CONFIG.BASE_URL}/meta`;

export interface ExchangeTokenData {
  code: string;
  state?: string;
}

export interface CreateBusinessAccountData {
  businessAccountId: string;
  accessToken: string;
  name: string;
  timezoneId?: string;
}

export interface UpdateAccessTokenData {
  businessAccountId: string;
  accessToken: string;
}

export interface GetTemplatesParams {
  businessAccountId: string;
  limit?: number;
  offset?: number;
}

export interface UploadMediaData {
  businessAccountId: string;
  file: File;
  type?: string;
}

export interface UploadMediaUrlData {
  businessAccountId: string;
  url: string;
  type?: string;
  filename?: string;
}

export interface BusinessProfileUpdatePayload {
  about?: string;
  description?: string;
  email?: string;
  address?: string;
  vertical?: string;
  websites?: string[];
  profile_picture_handle?: string;
}

export interface ConversationalAutomationCommand {
  command_name: string;
  command_description: string;
}

export type ConversationalAutomationCommandActionType = 'flow' | 'text' | 'url';

export interface ConversationalAutomationCommandInput {
  command_name: string;
  command_description: string;
  enabled?: boolean;
  action_type?: ConversationalAutomationCommandActionType;
  flow_id?: string;
  message?: string;
  url?: string;
}

export interface ConversationalAutomationCommandRow {
  command_name: string;
  command_description: string;
  enabled: boolean;
  action_type: ConversationalAutomationCommandActionType | null;
  flow_id: string | null;
  message: string | null;
  url: string | null;
  flow_name: string | null;
  flow_status: string | null;
  flow_missing: boolean;
}

export const CONVERSATIONAL_AUTOMATION_PROMPT_ACTIONS = ['flow', 'text', 'url'] as const;

export type ConversationalAutomationActionType =
  (typeof CONVERSATIONAL_AUTOMATION_PROMPT_ACTIONS)[number];

export interface ConversationalAutomationPromptInput {
  text: string;
  flow_id: string;
  action_type?: ConversationalAutomationActionType;
  message?: string;
  url?: string;
}

export interface ConversationalAutomationPrompt {
  text: string;
  flow_id: string | null;
  flow_name: string | null;
  flow_status: string | null;
  flow_missing: boolean;
  action_type: ConversationalAutomationActionType | null;
  message: string | null;
  url: string | null;
}

export interface ConversationalAutomationPayload {
  prompts?: ConversationalAutomationPromptInput[];
  commands?: ConversationalAutomationCommandInput[];
}

export const CONVERSATIONAL_AUTOMATION_LIMITS = {
  MAX_PROMPTS: 4,
  PROMPT_MAX_LENGTH: 80,
  MAX_COMMANDS: 30,
  COMMAND_NAME_MAX_LENGTH: 32,
  COMMAND_DESCRIPTION_MAX_LENGTH: 256,
} as const;

export const CONVERSATIONAL_AUTOMATION_COMMAND_ACTIONS = [
  'flow',
  'text',
  'url',
] as const;

export interface UploadBusinessProfileLogoResponse {
  success: boolean;
  handle?: string;
  data?: {
    handle?: string;
    url?: string;
  };
  error?: string;
}

export type MessagingUsageSource = 'campaign' | 'inbox' | 'automation' | 'api' | 'other';

export interface MessagingLimitInfo {
  tier: string | null;
  limit: number | null;
  isUnlimited: boolean;
  used: number;
  remaining: number | null;
  windowStart: string | null;
  quality_rating?: string | null;
  known?: boolean;
  assumed?: boolean;
  bySource?: Record<MessagingUsageSource, number> | null;
  nextFreeAt?: string | null;
  trackingSince?: string | null;
  trackingComplete?: boolean;
  tierSyncedAt?: string | null;
  sharedWith?: number;
}

export interface MessagingUsagePhoneNumber {
  id: string;
  display_phone_number: string;
  status?: string | null;
  verified_name?: string | null;
  messaging_limit_tier?: string | null;
  messaging_limit_info?: MessagingLimitInfo | null;
  quality_rating?: string | null;
}

export interface MessagingUsageSummary {
  businessAccountId: string;
  phoneNumbers: MessagingUsagePhoneNumber[];
  aggregate: MessagingLimitInfo;
}

export const metaApi = {
  async getConfig() {
    const response = await fetch(`${BASE_URL}/get-config`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to get Meta configuration');
    }

    return response.json();
  },

  async getPhoneNumbers(businessAccountRecordId: string) {
    const response = await fetch(`${BASE_URL}/whatsapp/phone-numbers/${businessAccountRecordId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to get phone numbers');
    }

    return response.json();
  },

  async syncChatHistory(businessAccountId: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
    details?: string;
    code?: string;
    history_sync_status?: string | null;
    history_sync_progress?: number | null;
    window_expires_at?: string | null;
    contact_sync_error?: string | null;
  }> {
    const token =
      localStorage.getItem('auth_token') ||
      sessionStorage.getItem('auth_token') ||
      localStorage.getItem('token');

    try {
      const response = await fetch(`${BASE_URL}/sync-chat-history`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ business_account_id: businessAccountId }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return {
          success: false,
          error: data?.error || 'Could not start the chat history import.',
          details: data?.details,
          code: data?.code,
          history_sync_status: data?.history_sync_status ?? null,
          history_sync_progress: data?.history_sync_progress ?? null,
          window_expires_at: data?.window_expires_at ?? null,
        };
      }
      return { success: true, ...data };
    } catch (err) {
      return {
        success: false,
        error: 'Could not reach the server to start the chat history import.',
        details: err instanceof Error ? err.message : undefined,
      };
    }
  },

  async dismissChatHistoryBanner(businessAccountId: string, bannerId: string) {
    const token =
      localStorage.getItem('auth_token') ||
      sessionStorage.getItem('auth_token') ||
      localStorage.getItem('token');

    const response = await fetch(`${BASE_URL}/dismiss-chat-history-banner`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ business_account_id: businessAccountId, banner_id: bannerId }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Could not dismiss the banner');
    }

    return response.json();
  },

  async getMessagingUsage(businessAccountRecordId: string) {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const response = await fetch(`${BASE_URL}/whatsapp/messaging-usage/${businessAccountRecordId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to get messaging usage');
    }

    return response.json();
  },

  async exchangeToken(data: ExchangeTokenData) {
    const response = await fetch(`${BASE_URL}/exchange-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to exchange token');
    }

    return response.json();
  },

  async getBusinessAccounts(accessToken: string) {
    const response = await fetch(`${BASE_URL}/business-accounts`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to get business accounts');
    }

    return response.json();
  },

  async createBusinessAccount(data: CreateBusinessAccountData) {
    const response = await fetch(`${BASE_URL}/create-business-account`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to create business account');
    }

    return response.json();
  },

  async updateAccessToken(data: UpdateAccessTokenData) {
    const response = await fetch(`${BASE_URL}/update-access-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to update access token');
    }

    return response.json();
  },

  async unlinkBusinessAccount(accountId: string) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/unlink-business-account/${accountId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to delink business account');
    }

    return response.json();
  },

  async getTemplates(params: GetTemplatesParams) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });

    const response = await fetch(`${BASE_URL}/templates?${queryParams.toString()}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to get templates');
    }

    return response.json();
  },

  async getTemplate(templateId: string, businessAccountId: string) {
    const response = await fetch(`${BASE_URL}/templates/${templateId}?businessAccountId=${businessAccountId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to get template');
    }

    return response.json();
  },


  async createTemplate(data: any) {
    const response = await fetch(`${BASE_URL}/templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to create template');
    }

    return response.json();
  },

  async deleteTemplate(templateId: string, businessAccountId: string) {
    const response = await fetch(`${BASE_URL}/templates/${templateId}?businessAccountId=${businessAccountId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to delete template');
    }

    return response.json();
  },

  async uploadMedia(data: UploadMediaData) {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('businessAccountId', data.businessAccountId);
    if (data.type) {
      formData.append('type', data.type);
    }

    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/whatsapp/media/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to upload media');
    }

    return response.json();
  },

  async uploadBusinessProfileLogo(accountId: string, file: File) {
    const token = localStorage.getItem('auth_token');
    const formData = new FormData();
    formData.append('logo', file);

    const response = await fetch(`${BASE_URL}/business-profile/${accountId}/logo`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to upload business profile logo');
    }

    return response.json() as Promise<UploadBusinessProfileLogoResponse>;
  },

  async uploadMediaUrl(data: UploadMediaUrlData) {
    const mediaResponse = await fetch(data.url);
    if (!mediaResponse.ok) {
      throw new Error(`Failed to download media from URL: ${mediaResponse.statusText}`);
    }

    const mediaBlob = await mediaResponse.blob();
    const file = new File([mediaBlob], data.filename || 'media', { type: mediaBlob.type });

    return this.uploadMedia({
      file,
      businessAccountId: data.businessAccountId,
      type: data.type
    });
  },

  async createResumableUploadSession(data: {
    businessAccountId: string;
    fileLength: number;
    fileType: string;
    fileName: string;
  }) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/whatsapp/resumable-upload/create-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to create upload session');
    }

    return response.json();
  },

  async uploadFileData(data: {
    businessAccountId: string;
    uploadId: string;
    fileData: ArrayBuffer;
    fileOffset?: number;
  }) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/whatsapp/resumable-upload/upload-data`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/octet-stream',
        'file-offset': (data.fileOffset || 0).toString(),
      },
      body: data.fileData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to upload file data');
    }

    return response.json();
  },

  async queryUploadStatus(data: {
    businessAccountId: string;
    uploadId: string;
  }) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/whatsapp/resumable-upload/status`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Cache-Control': 'no-cache',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to query upload status');
    }

    return response.json();
  },

  async uploadMediaWithResumableAPI(data: {
    businessAccountId: string;
    mediaUrl: string;
    mediaType: string;
  }) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/whatsapp/resumable-upload/upload-media`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to upload media with resumable API');
    }

    return response.json();
  },

  async getBusinessProfile(accountId: string) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/business-profile/${accountId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to fetch business profile');
    }

    return response.json();
  },

  async updateBusinessProfile(accountId: string, data: BusinessProfileUpdatePayload) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/business-profile/${accountId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to update business profile');
    }

    return response.json();
  },

  async getConversationalAutomation(accountId: string) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/conversational-automation/${accountId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to fetch conversational automation');
    }

    return response.json();
  },

  async updateConversationalAutomation(accountId: string, data: ConversationalAutomationPayload) {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${BASE_URL}/conversational-automation/${accountId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw ApiError.fromResult({ ...error, statusCode: response.status }, 'Failed to update conversational automation');
    }

    return response.json();
  },

};
